/**
 * Formatos de fecha del flujo de hotel, en UN solo lugar.
 *
 * El mismo viaje se estaba pintando con cuatro formatos distintos: `2026-08-26 - 2026-08-29`
 * en el formulario, `26 ago. – 29 ago.` en el resumen, `Miércoles, 26 Agosto 2026` en el
 * detalle (con mayúsculas que en español van en minúscula) y `23 de ago. de 2026` en las
 * políticas. Aquí viven los dos formatos oficiales — largo y corto — más el de la fecha
 * límite del proveedor.
 *
 * Ojo con la zona: una fecha de hotel NO es un instante. El check-in es el día 26 en el
 * hotel, sin importar desde dónde abra la app el viajero, así que estas funciones leen los
 * DÍGITOS literales y nunca convierten de zona.
 */

const MESES = [
	'enero',
	'febrero',
	'marzo',
	'abril',
	'mayo',
	'junio',
	'julio',
	'agosto',
	'septiembre',
	'octubre',
	'noviembre',
	'diciembre',
]

const MESES_CORTOS = [
	'ene',
	'feb',
	'mar',
	'abr',
	'may',
	'jun',
	'jul',
	'ago',
	'sep',
	'oct',
	'nov',
	'dic',
]

const DIAS = [
	'domingo',
	'lunes',
	'martes',
	'miércoles',
	'jueves',
	'viernes',
	'sábado',
]

interface Parts {
	year: number
	month: number
	day: number
	hour: number
	minute: number
	weekday: number
}

/**
 * Parte una fecha del backend o del proveedor tomando los dígitos tal cual.
 *
 * Acepta `2026-08-26`, `2026-08-26T23:59:00`, `2026-08-26T23:59:00Z` y
 * `2026-08-26T23:59:00+02:00`: en todos los casos el día y la hora que importan son los
 * que están escritos, no el instante al que equivalen. El offset se ignora a propósito —
 * cuando Hotelbeds dice `23:59:00+02:00` está diciendo "23:59 en el hotel".
 */
function parse(value: string | null | undefined): Parts | null {
	if (!value) return null

	const match = String(value).match(
		/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/
	)
	if (!match) return null

	const [, y, m, d, hh, mm] = match
	const year = Number(y)
	const month = Number(m)
	const day = Number(d)

	// Date sólo para el día de la semana; se construye en UTC para que no lo corra la zona.
	const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()

	return {
		year,
		month,
		day,
		hour: hh ? Number(hh) : 0,
		minute: mm ? Number(mm) : 0,
		weekday,
	}
}

/** "miércoles 26 de agosto de 2026" */
export function formatLongDate(value: string | null | undefined): string {
	const p = parse(value)
	if (!p) return '—'

	return `${DIAS[p.weekday]} ${p.day} de ${MESES[p.month - 1]} de ${p.year}`
}

/** "26 de agosto de 2026" (sin día de la semana) */
export function formatMediumDate(value: string | null | undefined): string {
	const p = parse(value)
	if (!p) return '—'

	return `${p.day} de ${MESES[p.month - 1]} de ${p.year}`
}

/** "26/08/2026", conservando los dígitos del calendario del hotel. */
export function formatShortDate(value: string | null | undefined): string {
	const p = parse(value)
	if (!p) return '—'

	return `${String(p.day).padStart(2, '0')}/${String(p.month).padStart(2, '0')}/${p.year}`
}

/** "26 – 29 ago 2026" · "29 ago – 2 sep 2026" cuando cambia el mes o el año */
export function formatDateRange(
	start: string | null | undefined,
	end: string | null | undefined
): string {
	const a = parse(start)
	const b = parse(end)
	if (!a || !b) return '—'

	if (a.year === b.year && a.month === b.month) {
		return `${a.day} – ${b.day} ${MESES_CORTOS[a.month - 1]} ${a.year}`
	}

	if (a.year === b.year) {
		return `${a.day} ${MESES_CORTOS[a.month - 1]} – ${b.day} ${MESES_CORTOS[b.month - 1]} ${a.year}`
	}

	return `${formatShortDate(start)} – ${formatShortDate(end)}`
}

/**
 * Fecha límite de cancelación del proveedor, con la hora, que es justo lo que decide si
 * cobran penalización: "23/08/2026 23:59 (hora del hotel)".
 *
 * Antes se imprimía el ISO crudo del proveedor (`2026-08-23T23:59:00+02:00`).
 */
export function formatProviderDeadline(
	value: string | null | undefined
): string {
	const p = parse(value)
	if (!p) return '—'

	const fecha = formatShortDate(value)
	// Sin hora explícita no se inventa: se muestra sólo el día.
	if (!String(value).includes('T') && !String(value).includes(' ')) {
		return fecha
	}

	const hora = `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`

	return `${fecha} ${hora} (hora del hotel)`
}
